import React, { useEffect, useRef, useState } from "react";
import { Accordion, ActionDropdown, IconButton } from 'components/common';
import { useAppLogo } from "helpers/useAppSetting";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from 'prop-types';
import { FaPlus, FaEdit, FaTrashAlt } from "react-icons/fa";
import TinymceEditor from "components/common/TinymceEditor";
import { INPUT_CLS, TEXTAREA_CLS, ERROR_CLS, CHECK_CLS, TH_CLS, TD_CLS, SECTION_BODY_CLS, VALUE_CLS } from "../pifStyles";

const CHECK_LABEL_CLS = "tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none";

const GDS = ({
  projectData,
  gdsData,
  pifId,
  useHookForm,
  paintSystemData,
  newPaintSystem,
  editPaintSystem,
  deletePaintSystem,
  secondaryMemberData,
  newSecondaryMember,
  editSecondaryMember,
  deleteSecondaryMember
}) => {
  const logo = useAppLogo();
  const {gds: initialKeys} = useSelector(state => state.crm_project_pif.initialKeys);
  const [activeKey, setActiveKey] = useState(initialKeys);

  const {project, building, adaptation} = projectData;
  const {control, register, errors, Controller, setValue} = useHookForm || {};

  useEffect(() => {
    setActiveKey(initialKeys);
  }, [initialKeys]);

  useEffect(() => {
    setValue("gds.no_of_bldgs", 1)
    setValue("gds.no_of_non_identical_bldgs", 1)
  }, [setValue])

  // Seed the GDS fields from the server snapshot EXACTLY ONCE.
  //
  // This used to re-run whenever `gdsData` changed identity, which happened on
  // every paint-system / secondary-member row add, edit or delete — re-seeding
  // all of `gds.*` and silently reverting whatever the user had just typed.
  // `pif` arrives asynchronously, so the guard keys on its id rather than on the
  // first render.
  const seededGdsIdRef = useRef(null);

  useEffect(() => {
    if (!gdsData) return;

    const seedKey = String(pifId ?? 'new');
    if (seededGdsIdRef.current === seedKey) return;
    seededGdsIdRef.current = seedKey;

    const DATE_KEYS = [
      'contract_loi_on_date',
      'peb_standard_offer_signed_date',
      'customer_contract_signed_date',
      'checked_date'
    ];
    // Both lists live in their own state and are merged back in at submit time.
    const OWNED_ELSEWHERE = ['paint_systems', 'secondary_members'];

    Object.entries(gdsData).forEach(([key, value]) => {
      if (!value || OWNED_ELSEWHERE.includes(key)) return;

      setValue(`gds.${key}`, DATE_KEYS.includes(key) ? new Date(value) : value);
    });
  }, [gdsData, pifId, setValue]);

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  return (
    <Accordion
      activeKeys={activeKey}
      onChange={handleAccordionSelect}
      className="tw-text-sm"
    >
      {/* 1. General Information */}
      <Accordion.Item eventKey="0" header="1. General Information">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-0">
            <div className="tw-col-span-2">
              <img
                src={logo}
                alt="Logo Pebsteel"
                className="tw-object-contain tw-rounded-sm"
                width="160"
                height="60"
                style={{ maxWidth: '100%' }}
              />
            </div>
            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-5">
                  Quote No:
                </div>
                <div className="tw-col-span-7">
                  <span className={VALUE_CLS}>{project?.q_number}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-5">
                  P/Job No:
                </div>
                <div className="tw-col-span-7">
                  <span className={VALUE_CLS}>{building?.j_number}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  CO#:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{adaptation?.co || '00'}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  REV#:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{adaptation?.rev}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Date:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{adaptation?.entry_date}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Date:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{adaptation?.entry_date}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-4">
              <div className="tw-flex tw-justify-between">
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    name="for_order_entry"
                  />
                  <span>For Order Entry</span>
                </label>

                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    name="for_quotation"
                    checked={true}
                  />
                  <span>For Quotation</span>
                </label>
              </div>
              <h6 className="tw-text-center tw-my-2">This sheet is applicable to entire project</h6>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mt-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Project name:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{project?.name}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">
                  Location:
                </div>
                <div className="tw-col-span-8">
                  <span className={VALUE_CLS}>{building?.pj_location}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-6">
                  No of all Bldgs in this Project:
                </div>
                <div className="tw-col-span-6">
                  <input
                    type="number"
                    placeholder="Number of building"
                    className={INPUT_CLS}
                    {...register("gds.no_of_bldgs", {
                      required: "No of all Bldgs in this project must be required."
                    })}
                  />
                  {errors["gds.no_of_bldgs"] && <p className={ERROR_CLS}>{errors["gds.no_of_bldgs"].message}</p>}
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-6">
                  No. of Non Identical Bldgs in this Project:
                </div>
                <div className="tw-col-span-6">
                  <input
                    type="number"
                    placeholder="No. of Non Identical Bldgs"
                    className={INPUT_CLS}
                    {...register("gds.no_of_non_identical_bldgs", {
                      required: "No. of Non Identical Bldgs in this project must be required."
                    })}
                  />
                  {errors["gds.no_of_non_identical_bldgs"] && <p className={ERROR_CLS}>{errors["gds.no_of_non_identical_bldgs"].message}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 2. Current Contract status */}
      <Accordion.Item eventKey="1" header="2. Current Contract status">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.contract_loi"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Letter of intent issued by customer on (Date)</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.contract_loi_on_date"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={d => field.onChange(adjustToNoon(d))}
                    className={INPUT_CLS}
                    wrapperClassName="tw-w-full tw-block"
                    placeholderText="DD-MMM-YYYY"
                    dateFormat="dd-MMM-yyyy"
                    fixedHeight
                  />
                )}
              />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.peb_standard_offer_signed"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>PEB Standard Offer signed by customer on (Date)</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.peb_standard_offer_signed_date"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={d => field.onChange(adjustToNoon(d))}
                    className={INPUT_CLS}
                    wrapperClassName="tw-w-full tw-block"
                    placeholderText="DD-MMM-YYYY"
                    dateFormat="dd-MMM-yyyy"
                    fixedHeight
                  />
                )}
              />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.customer_contract_signed"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Customer contract signed by customer on (Date)</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.customer_contract_signed_date"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={d => field.onChange(adjustToNoon(d))}
                    className={INPUT_CLS}
                    wrapperClassName="tw-w-full tw-block"
                    placeholderText="DD-MMM-YYYY"
                    dateFormat="dd-MMM-yyyy"
                    fixedHeight
                  />
                )}
              />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.contract_status_others"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Others (Explain)</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-col-span-6">
              <textarea
                rows={5}
                placeholder="Explain"
                className={TEXTAREA_CLS}
                {...register("gds.contract_status_others_explain")}
              />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 3. Penalty */}
      <Accordion.Item eventKey="2" header="3. Penalty">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Terms of penalty
            </div>
            <div className="tw-col-span-8">
              <textarea
                placeholder="Terms of penalty"
                className={TEXTAREA_CLS}
                {...register("gds.term_of_penalty")}
              />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-mt-3">
            <div className="tw-col-span-3">
              Max. Penalty as % of Contract Price:
            </div>
            <div className="tw-col-span-3">
              <Controller
                control={control}
                name="gds.penalty_of_contact_price_5_percent"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>5%</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-col-span-6">
              <Controller
                control={control}
                name="gds.penalty_of_contact_price_other"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Others (Specify)</span>
                  </label>
                )}
              />

              <textarea
                placeholder="Others (Specify)"
                className={TEXTAREA_CLS}
                {...register("gds.penalty_of_contact_price_other_specify")}
              />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 4. Shipment */}
      <Accordion.Item eventKey="3" header="4. Shipment">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Delivery Commitment (weeks):
            </div>
            <div className="tw-col-span-8">
              <input
                type="number"
                placeholder="Delivery Commitment (weeks)"
                className={INPUT_CLS}
                {...register("gds.shipment_delivery_commitment")}
              />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Delivery by:
            </div>
            <div className="tw-col-span-8">
              <div className="tw-flex tw-justify-between">
                <Controller
                  control={control}
                  name="gds.shipment_delivery_by_pebsteel"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>by PEB Steel</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="gds.shipment_customer_pickup"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>Customer pickup</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="gds.shipment_partial_allow"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>Partial shipment allowed</span>
                    </label>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-0 tw-items-center">
                <div className="tw-col-span-3">
                  <Controller
                    control={control}
                    name="gds.overland_to"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Overland to</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Overland to"
                    className={INPUT_CLS}
                    {...register("gds.shipment_overland_to")}
                  />
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-0 tw-items-center">
                <div className="tw-col-span-3">
                  <Controller
                    control={control}
                    name="gds.seafreight_to"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Seafreight to</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Seafreight to"
                    className={INPUT_CLS}
                    {...register("gds.shipment_seafreight_to")}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.shipment_seafreight"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Seafreight</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.shipment_container"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Container</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.shipment_truck"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Truck</span>
                      </label>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-4">
                  Export document by:
                </div>
                <div className="tw-col-span-8">
                  <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                    <div className="tw-col-span-4">
                      <Controller
                        control={control}
                        name="gds.shipment_export_document_by_pebsteel"
                        render={({ field }) => (
                          <label className={CHECK_LABEL_CLS}>
                            <input
                              type="checkbox"
                              className={CHECK_CLS}
                              checked={!!field.value}
                              onChange={(value) => field.onChange(value)}
                            />
                            <span>PEB Steel</span>
                          </label>
                        )}
                      />
                    </div>
                    <div className="tw-col-span-8">
                      <Controller
                        control={control}
                        name="gds.shipment_export_document_by_others"
                        render={({ field }) => (
                          <label className={CHECK_LABEL_CLS}>
                            <input
                              type="checkbox"
                              className={CHECK_CLS}
                              checked={!!field.value}
                              onChange={(value) => field.onChange(value)}
                            />
                            <span>by others</span>
                          </label>
                        )}
                      />

                      <textarea
                        placeholder="Export document by others"
                        className={TEXTAREA_CLS}
                        {...register("gds.shipment_export_document_by_others_specify")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 5. Required Initial Engineering Submittals */}
      <Accordion.Item eventKey="4" header="5. Required Initial Engineering Submittals">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <Controller
                control={control}
                name="gds.eng_design_calculation"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Design calculation</span>
                  </label>
                )}
              />

              <Controller
                control={control}
                name="gds.eng_approval_drawings"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Approval drawings</span>
                  </label>
                )}
              />

              <Controller
                control={control}
                name="gds.eng_erection_drawings"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Erection drawings</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-flex-1">
              <Controller
                control={control}
                name="gds.end_preliminary_design_sheet"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Preliminary Design Sheet</span>
                  </label>
                )}
              />

              <Controller
                control={control}
                name="gds.eng_anchor_bolts_plan"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Anchor Bolts Plan</span>
                  </label>
                )}
              />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 6. Anchor Bolts */}
      <Accordion.Item eventKey="5" header="6. Anchor Bolts">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Anchor Bolts by:
            </div>
            <div className="tw-col-span-8">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.anchor_bolts_by_pebsteel"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>PEB Steel</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.anchor_bolts_by_other"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>by others</span>
                      </label>
                    )}
                  />

                  <textarea
                    placeholder="Others"
                    className={TEXTAREA_CLS}
                    {...register("gds.anchor_bolts_by_other_specify")}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mt-3">
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">Ship Anchor Bolts:</div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.anchor_bolts_ship_with_job"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>with Job</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.anchor_bolts_priod_to_job"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Priod to Job</span>
                      </label>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="tw-col-span-6">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">Ship by:</div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.anchor_bolts_ship_by_overland"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Overland</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.anchor_bolts_ship_by_seafreight"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Seafreight</span>
                      </label>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="tw-mt-2">
            <textarea
              placeholder="Note for anchor bolts"
              className={TEXTAREA_CLS}
              {...register("gds.anchor_bolts_note")}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 7. Buyer (Invoice to) */}
      <Accordion.Item eventKey="6" header="7. Buyer (Invoice to)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                  <Link to={`/crm/customers/${project?.customer?.id}`} target="_blank">{project?.customer?.name}</Link>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Phone:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>{project?.contact?.mobile}</div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Address:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>{project?.customer?.primary_address}</div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Contact person:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>{project?.contact?.name}</div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Email:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>{project?.contact?.email}</div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Fax:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>{project?.contact?.fax}</div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Title:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>{project?.contact?.position}</div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 8. Consignee (Ship To)  */}
      <Accordion.Item eventKey="7" header="8. Consignee (Ship To)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Name:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Name"
                    className={INPUT_CLS}
                    {...register("gds.consignee_name")}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Phone:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Phone"
                    className={INPUT_CLS}
                    {...register("gds.consignee_phone")}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Address:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Address"
                    className={INPUT_CLS}
                    {...register("gds.consignee_address")}
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Mobile:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Mobile"
                    className={INPUT_CLS}
                    {...register("gds.consignee_mobile")}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Fax:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Fax"
                    className={INPUT_CLS}
                    {...register("gds.consignee_fax")}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Email:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Email"
                    className={INPUT_CLS}
                    {...register("gds.consignee_email")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 9. Shipment (Advice To) */}
      <Accordion.Item eventKey="8" header="9. Shipment (Advice To)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Name:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Name"
                    className={INPUT_CLS}
                    {...register("gds.shipment_advice_to_name")}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Fax:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Fax"
                    className={INPUT_CLS}
                    {...register("gds.shipment_advice_to_fax")}
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Mobile:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Mobile"
                    className={INPUT_CLS}
                    {...register("gds.shipment_advice_to_mobile")}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Email:</div>
                <div className="tw-col-span-9">
                  <input
                    type="text"
                    placeholder="Email"
                    className={INPUT_CLS}
                    {...register("gds.shipment_advice_to_email")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 10. Contractor */}
      <Accordion.Item eventKey="9" header="10. Contractor">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                  <Link to={`/crm/customers/${project?.contractor?.id}`} target="_blank">{project?.contractor?.name}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 11. Consultant */}
      <Accordion.Item eventKey="10" header="11. Consultant">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                  <Link to={`/crm/customers/${project?.consultant?.id}`} target="_blank">{project?.consultant?.name}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 12. Enduser */}
      <Accordion.Item eventKey="11" header="12. Enduser">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-6">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-3">Name:</div>
                <div className={`tw-col-span-9 ${VALUE_CLS}`}>
                  <Link to={`/crm/customers/${project?.enduser?.id}`} target="_blank">{project?.enduser?.name}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 13. Erection */}
      <Accordion.Item eventKey="12" header="13. Erection">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              Erection by:
            </div>
            <div className="tw-col-span-8">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-3">
                  <Controller
                    control={control}
                    name="gds.erection_by_pebsteel"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>PEB Steel</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-col-span-3">
                  <Controller
                    control={control}
                    name="gds.erection_by_others"
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>by others</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-col-span-6">
                  <div className="tw-flex tw-gap-x-2">
                    <div>
                      Erector Name:
                    </div>
                    <div className="tw-flex-1">
                      <textarea
                        placeholder="Erector Name"
                        className={TEXTAREA_CLS}
                        {...register("gds.erector_name")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-1">
            <div className="tw-flex-1">
              <Controller
                control={control}
                name="gds.erection_supervisor_by_peb"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Erection supervisior by PEB</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-gap-x-2">
                <div>
                  Explain details:
                </div>
                <div className="tw-flex-1">
                  <textarea
                    placeholder="Explain details"
                    className={TEXTAREA_CLS}
                    {...register("gds.erection_explain_detail")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Printed on the PDF since forever, but there was no way to fill it. */}
          <div className="tw-flex tw-gap-x-2 tw-mt-2">
            <div>
              Note:
            </div>
            <div className="tw-flex-1">
              <textarea
                placeholder="Erection note"
                className={TEXTAREA_CLS}
                {...register("gds.erection_note")}
              />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 14. Primary Members */}
      <Accordion.Item eventKey="13" header="14. Primary Members">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div>
              Surface treatment:
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-justify-between">
                <Controller
                  control={control}
                  name="gds.primary_power_clean_st3"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>Power clean ST3</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="gds.primary_shotblasting_sa_2_0"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>Shotblasting SA 2.0</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="gds.primary_shotblasting_sa_2_5"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>Shotblasting SA 2.5</span>
                    </label>
                  )}
                />
                <Controller
                  control={control}
                  name="gds.primary_hd_galv"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>H.D.Galv</span>
                    </label>
                  )}
                />
                <Controller
                  control={control}
                  name="gds.primary_dft"
                  render={({ field }) => (
                    <label className={CHECK_LABEL_CLS}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        checked={!!field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                      <span>DFT</span>
                    </label>
                  )}
                />
              </div>
            </div>
          </div>
          <div className="tw-flex">
            <div className="tw-mr-3">Purlin Clip to Column / Rafter:</div>
            <Controller
              control={control}
              name="gds.primary_purlin_clip_to_column"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                </label>
              )}
            />
          </div>
          <div className="tw-mt-2">
            <textarea
              rows={5}
              placeholder="Specify notes..."
              className={TEXTAREA_CLS}
              {...register("gds.primary_memebers_note")}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 15. Paint system */}
      <Accordion.Item eventKey="14" header="15. Paint system">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={newPaintSystem}
            >
              <span className="tw-hidden xl:tw-inline-block">New Paint System</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr>
                  <th className={TH_CLS}></th>
                  <th className={TH_CLS}>Coat No.</th>
                  <th className={TH_CLS}>DFT (microns)</th>
                  <th className={TH_CLS}>Paint Type</th>
                  <th className={TH_CLS}>Manufacturer</th>
                  <th className={TH_CLS}>Color</th>
                </tr>
              </thead>
              <tbody>
                {paintSystemData && paintSystemData.map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          { label: 'Edit', icon: <FaEdit size={12} />, onClick: () => editPaintSystem(item) },
                          { divider: true },
                          { label: 'Delete', icon: <FaTrashAlt size={12} />, onClick: () => deletePaintSystem(item), danger: true },
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.cost_no}</td>
                    <td className={TD_CLS}>{item.dft}</td>
                    <td className={TD_CLS}>{item.paint_type}</td>
                    <td className={TD_CLS}>{item.manufacturer}</td>
                    <td className={TD_CLS}>{item.color}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-2">
            <Controller
              control={control}
              name="gds.paint_system_note"
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 16. Secondary Members (Purlins, girts, eave strut) */}
      <Accordion.Item eventKey="15" header="16. Secondary Members (Purlins, girts, eave strut)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-3">
              <Controller
                control={control}
                name="gds.second_galv"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Galv.</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-col-span-9">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-3 tw-font-semibold">
                  Surface treatment:
                </div>
                <div className="tw-col-span-9">
                  <div className="tw-flex tw-justify-between">
                    <Controller
                      control={control}
                      name="gds.second_power_clean_st3"
                      render={({ field }) => (
                        <label className={CHECK_LABEL_CLS}>
                          <input
                            type="checkbox"
                            className={CHECK_CLS}
                            checked={!!field.value}
                            onChange={(value) => field.onChange(value)}
                          />
                          <span>Power clean ST3</span>
                        </label>
                      )}
                    />
                    <Controller
                      control={control}
                      name="gds.second_shotblasting_sa_2_0"
                      render={({ field }) => (
                        <label className={CHECK_LABEL_CLS}>
                          <input
                            type="checkbox"
                            className={CHECK_CLS}
                            checked={!!field.value}
                            onChange={(value) => field.onChange(value)}
                          />
                          <span>Shotblasting SA 2.0</span>
                        </label>
                      )}
                    />
                    <Controller
                      control={control}
                      name="gds.second_shotblasting_sa_2_5"
                      render={({ field }) => (
                        <label className={CHECK_LABEL_CLS}>
                          <input
                            type="checkbox"
                            className={CHECK_CLS}
                            checked={!!field.value}
                            onChange={(value) => field.onChange(value)}
                          />
                          <span>Shotblasting SA 2.5</span>
                        </label>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mt-2">
                <div className="tw-col-span-3 tw-font-semibold">
                  Paint system:
                </div>
                <div className="tw-col-span-9">
                  <div className="tw-flex tw-justify-between">
                    <Controller
                      control={control}
                      name="gds.second_paint_standard"
                      render={({ field }) => (
                        <label className={CHECK_LABEL_CLS}>
                          <input
                            type="checkbox"
                            className={CHECK_CLS}
                            checked={!!field.value}
                            onChange={(value) => field.onChange(value)}
                          />
                          <span>Standard Alkyd red oxide primer</span>
                        </label>
                      )}
                    />
                    <Controller
                      control={control}
                      name="gds.second_paint_speical"
                      render={({ field }) => (
                        <label className={CHECK_LABEL_CLS}>
                          <input
                            type="checkbox"
                            className={CHECK_CLS}
                            checked={!!field.value}
                            onChange={(value) => field.onChange(value)}
                          />
                          <span>Special (see below)</span>
                        </label>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-end tw-mt-3">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={newSecondaryMember}
            >
              <span className="tw-hidden xl:tw-inline-block">New</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr>
                  <th className={TH_CLS}></th>
                  <th className={TH_CLS}>Coat No.</th>
                  <th className={TH_CLS}>DFT (microns)</th>
                  <th className={TH_CLS}>Paint Type</th>
                  <th className={TH_CLS}>Manufacturer</th>
                  <th className={TH_CLS}>Color</th>
                </tr>
              </thead>
              <tbody>
                {secondaryMemberData && secondaryMemberData.map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          { label: 'Edit', icon: <FaEdit size={12} />, onClick: () => editSecondaryMember(item) },
                          { divider: true },
                          { label: 'Delete', icon: <FaTrashAlt size={12} />, onClick: () => deleteSecondaryMember(item), danger: true },
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.cost_no}</td>
                    <td className={TD_CLS}>{item.dft}</td>
                    <td className={TD_CLS}>{item.paint_type}</td>
                    <td className={TD_CLS}>{item.manufacturer}</td>
                    <td className={TD_CLS}>{item.color}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-2">
            <Controller
              control={control}
              name="gds.secondary_memeber_note"
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 17. Attached */}
      <Accordion.Item eventKey="16" header="17. Attached">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-between tw-mx-4">
            <Controller
              control={control}
              name="gds.attached_gds"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>GDS</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.attached_bds1"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>BDS1</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.attached_bds2"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>BDS2</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.attached_bds3"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>BDS3</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.attached_sketchs"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Sketchs</span>
                </label>
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 18. Checking */}
      <Accordion.Item eventKey="17" header="18. Checking">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-x-2">
                <div>Filled by:</div>
                <div className="tw-flex-1">
                  <input
                    type="text"
                    placeholder="Filled by"
                    className={INPUT_CLS}
                    {...register("gds.filled_by")}
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-x-2">
                <div>Checked by:</div>
                <div className="tw-flex-1">
                  <input
                    type="text"
                    placeholder="Checked by"
                    className={INPUT_CLS}
                    {...register("gds.checked_by")}
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-x-2">
                <div>Date:</div>
                <div className="tw-flex-1">
                  <Controller
                    control={control}
                    name="gds.checked_date"
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={d => field.onChange(adjustToNoon(d))}
                        className={INPUT_CLS}
                        wrapperClassName="tw-w-full tw-block"
                        placeholderText="DD-MMM-YYYY"
                        dateFormat="dd-MMM-yyyy"
                        fixedHeight
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 19. Double Side Welding - UT Test - Third Part Inspection */}
      <Accordion.Item eventKey="18" header="19. Double Side Welding - UT Test - Third Part Inspection">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2">
            <div>
              <Controller
                control={control}
                name="gds.double_side_welding"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Double Side Welding</span>
                  </label>
                )}
              />
              <Controller
                control={control}
                name="gds.ut_test"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>UT Test</span>
                  </label>
                )}
              />
              <Controller
                control={control}
                name="gds.third_party_inspection"
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Third Party Inspection</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-flex-1">
              <textarea
                rows={5}
                placeholder="Specify notes..."
                className={TEXTAREA_CLS}
                {...register("gds.double_side_welding_notes")}
              />
            </div>
          </div>

          <div className="tw-flex tw-justify-between tw-mt-3">
            <Controller
              control={control}
              name="gds.engineering"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Engineering</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.sales_support_dept"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Sales Support Dept,</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.sales_dept"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Sales Dept,</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.customer_service_dept"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Customer Service Dept,</span>
                </label>
              )}
            />

            <Controller
              control={control}
              name="gds.production"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Production,</span>
                </label>
              )}
            />
            <Controller
              control={control}
              name="gds.finance"
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>Finance,</span>
                </label>
              )}
            />
          </div>
        </div>
      </Accordion.Item>
    </Accordion>
  )
};

GDS.propTypes = {
  projectData: PropTypes.object.isRequired,
  gdsData: PropTypes.object.isRequired,
  pifId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  useHookForm: PropTypes.object.isRequired,
  paintSystemData: PropTypes.arrayOf(PropTypes.object),
  newPaintSystem: PropTypes.func,
  editPaintSystem: PropTypes.func,
  deletePaintSystem: PropTypes.func,
  secondaryMemberData: PropTypes.arrayOf(PropTypes.object),
  newSecondaryMember: PropTypes.func,
  editSecondaryMember: PropTypes.func,
  deleteSecondaryMember: PropTypes.func,
}

export default GDS;
